import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
  sidebarCollapsed: boolean;
  currentPage: string;
  breadcrumb: {
    name: string;
    path?: string;
  }[];
  loading: {
    [key: string]: boolean;
  };
  modals: {
    [key: string]: {
      visible: boolean;
      data?: any;
    };
  };
  notifications: {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    duration?: number;
  }[];
  theme: 'light' | 'dark';
  language: 'zh-CN' | 'en-US';
}

const initialState: UIState = {
  sidebarCollapsed: false,
  currentPage: 'dashboard',
  breadcrumb: [],
  loading: {},
  modals: {},
  notifications: [],
  theme: 'light',
  language: 'zh-CN',
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // 侧边栏控制
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
    },

    // 当前页面
    setCurrentPage: (state, action: PayloadAction<string>) => {
      state.currentPage = action.payload;
    },

    // 面包屑导航
    setBreadcrumb: (state, action: PayloadAction<UIState['breadcrumb']>) => {
      state.breadcrumb = action.payload;
    },

    // 加载状态
    setLoading: (state, action: PayloadAction<{ key: string; loading: boolean }>) => {
      state.loading[action.payload.key] = action.payload.loading;
    },
    clearLoading: (state, action: PayloadAction<string>) => {
      delete state.loading[action.payload];
    },

    // 模态框控制
    showModal: (state, action: PayloadAction<{ key: string; data?: any }>) => {
      state.modals[action.payload.key] = {
        visible: true,
        data: action.payload.data,
      };
    },
    hideModal: (state, action: PayloadAction<string>) => {
      if (state.modals[action.payload]) {
        state.modals[action.payload].visible = false;
      }
    },
    closeModal: (state, action: PayloadAction<string>) => {
      delete state.modals[action.payload];
    },

    // 通知管理
    addNotification: (state, action: PayloadAction<Omit<UIState['notifications'][0], 'id'>>) => {
      const notification = {
        ...action.payload,
        id: Date.now().toString(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // 主题切换
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },

    // 语言切换
    setLanguage: (state, action: PayloadAction<'zh-CN' | 'en-US'>) => {
      state.language = action.payload;
    },

    // 重置UI状态
    resetUI: (state) => {
      state.sidebarCollapsed = false;
      state.currentPage = 'dashboard';
      state.breadcrumb = [];
      state.loading = {};
      state.modals = {};
      state.notifications = [];
    },
  },
});

export const {
  toggleSidebar,
  setSidebarCollapsed,
  setCurrentPage,
  setBreadcrumb,
  setLoading,
  clearLoading,
  showModal,
  hideModal,
  closeModal,
  addNotification,
  removeNotification,
  clearNotifications,
  setTheme,
  setLanguage,
  resetUI,
} = uiSlice.actions;

export default uiSlice.reducer; 